import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';
import AbbrTip from '@/components/AbbrTip';

const FAQS = [
  {
    q: 'When should I sell a dividend stock?',
    a: 'Six clean triggers: (1) the dividend is cut or suspended; (2) the payout ratio has been above 100% for more than two quarters; (3) free cash flow can no longer cover the dividend; (4) the original thesis is broken (regulatory, technological, or competitive); (5) tax-loss harvesting opportunity that beats the future income; (6) a clearly better opportunity. Anything outside those six is usually noise.',
  },
  {
    q: 'Should I sell after a dividend cut?',
    a: 'Usually, yes — but not immediately on the announcement. The stock has already dropped 10–30% in pre-market reaction. Wait 1–2 trading days for the panic to settle, then decide based on whether the cut signals a one-time reset (GE 2018, Disney 2020 pandemic) or a structural problem (AT&T 2022, Walgreens 2024). One-time resets sometimes rebuild; structural cuts usually erode further.',
  },
  {
    q: 'What if the stock is up a lot — should I take profits?',
    a: 'For a long-term dividend portfolio, generally no. Selling a winning Aristocrat resets your Yield on Cost and triggers a tax bill on the realised gain. If your concern is sector concentration (the stock has grown to 15%+ of the portfolio), trim back to your target weight rather than selling the whole position. DiviDrip’s Income Concentration banner flags positions that have grown into outsized portfolio weights.',
  },
  {
    q: 'What about the "never sell" school of thought?',
    a: 'Buy-and-hold-forever works fantastically when the company keeps compounding. It fails catastrophically when management destroys value (Kraft Heinz, GE, AT&T after the WarnerMedia spin). Even Warren Buffett sells when the thesis breaks (he sold every airline in April 2020). "Never sell" is shorthand for "do not sell because the price wobbled" — not "do not sell when the business breaks".',
  },
  {
    q: 'Does DiviDrip warn me before a stock should be sold?',
    a: 'Yes, three ways. The Dividend Triangle Score downgrades stocks toward TRAP / RISK / WEAK as the underlying fundamentals deteriorate — falling FCF coverage, rising payout ratio, breaking dividend streak. The Streak Risk Chart visually flags when annual dividend growth has stalled. And the SEC Filings Alerts in the Notifications panel surface 8-K material-events filings (often the first signal of an upcoming cut). None of these are sell signals on their own; they’re prompts to re-evaluate.',
  },
  {
    q: 'What’s the tax cost of selling a dividend stock in a taxable account?',
    a: 'Long-term capital gains (held more than 1 year) are taxed at 0% / 15% / 20% depending on your income bracket. Short-term gains are taxed as ordinary income (10–37%). If the position has an unrealised loss, you can harvest it to offset gains or up to $3,000 of ordinary income per year — see the "Tax-Loss Harvesting Playbook" guide for the wash-sale rules and worked examples. Inside a Roth IRA or 401(k), selling has zero tax consequence.',
  },
];

export default function LearnWhenToSell() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <LearnPage
      title="When Should You Sell a Dividend Stock? The 6 Clean Triggers (2026) | DiviDrip"
      description="A practical sell framework for dividend investors. Six clean triggers — cut, payout ratio, broken thesis, tax-loss harvest, sector concentration, better opportunity. When buy-and-hold-forever stops being optimal."
      canonical="https://twylightcrow.com/learn/when-to-sell-a-dividend-stock"
      guidePath="/learn/when-to-sell-a-dividend-stock"
      sectionLabel="Strategy & Psychology"
      sectionAnchor="strategy"
      kicker="Learn · Sell discipline"
      h1="When Should You Sell a Dividend Stock?"
      datePublished="2026-06-14"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/dividend-cuts-of-the-decade', label: 'Dividend cuts of the decade — 12 cuts decoded' },
        { to: '/learn/dividend-traps', label: 'Dividend trap stocks — spotting yields that lie' },
        { to: '/learn/payout-ratio', label: 'The payout ratio — your first safety check' },
        { to: '/learn/tax-loss-harvesting-playbook', label: 'The tax-loss harvesting playbook' },
        { to: '/learn/cost-basis-methods-fifo-lifo-hifo', label: 'Choosing your cost-basis method' },
        { to: '/learn/dividend-triangle', label: 'Reading the Dividend Triangle score' },
        { to: '/learn/four-phases', label: 'The 4 phases of a dividend investor’s life' },
      ]}
    >
      <p>
        Most dividend investing content is about <em>buying</em>. Almost none of it
        is about <em>selling</em>. That gap is dangerous &mdash; the difference
        between &quot;buy and hold forever&quot; and &quot;buy, hold, and sell
        when the thesis breaks&quot; is the difference between holding GE for the
        2000s &mdash; or Walgreens for the 2020s &mdash; and avoiding the
        50&ndash;70% drawdown plus 47&ndash;48% dividend cut.
      </p>
      <p>
        This guide gives you six clean sell triggers. If a position does not
        meet at least one of these, the default is &quot;keep holding.&quot;
      </p>

      <h2>Trigger 1 — The dividend is cut or suspended</h2>
      <p>
        This is the only trigger that is almost mechanical. A dividend cut is
        management telling you the business is not throwing off enough cash to
        support the payment any more. Walk through the recent history:
      </p>
      <ul>
        <li><strong>GE</strong> cut 50% in 2017 and 92% in 2018. The stock continued to fall for years after.</li>
        <li><strong>Kraft Heinz</strong> cut 36% in 2019. The yield never recovered; the stock has chopped sideways for years.</li>
        <li><strong>AT&amp;T</strong> cut 47% in 2022 alongside the WarnerMedia spin. Investors who held got a smaller dividend and a smaller stock.</li>
        <li><strong>Walgreens</strong> cut 48% in 2024 after years of warning signs. The stock dropped 70%+ over 18 months.</li>
        <li><strong>Intel</strong> suspended the dividend entirely in 2023. The 30-year streak vanished.</li>
      </ul>
      <p>
        In all five, the right move was: sit through the immediate one-day
        panic, then sell on day two or three once volume normalised. Cuts
        almost always signal more pain ahead, not a buying opportunity.
      </p>

      <h2>Trigger 2 — <AbbrTip term="PAYOUT_RATIO">Payout ratio</AbbrTip> &gt; 100% for two quarters running</h2>
      <p>
        A company paying out more than it earns is funding the dividend from
        debt, asset sales, or balance-sheet cash. None of these can run
        forever. A single bad quarter above 100% is sometimes noise. Two in a
        row is a yellow flag, three is red.
      </p>
      <p>
        Sector caveat: <AbbrTip term="REIT">REITs</AbbrTip> and{' '}
        <AbbrTip term="BDC">BDCs</AbbrTip> have their own metrics (
        <AbbrTip term="AFFO">AFFO</AbbrTip> payout for REITs,{' '}
        <AbbrTip term="NII">NII</AbbrTip> coverage for BDCs). A REIT with a 95%
        earnings payout might have a comfortable 75% AFFO payout. See{' '}
        <Link to="/learn/payout-ratio">the payout ratio guide</Link> for the
        sector-specific math.
      </p>
      <p>
        DiviDrip&apos;s Stock Modal Dividend Info tab color-codes the{' '}
        <AbbrTip term="PAYOUT_RATIO">payout ratio</AbbrTip> (green / amber /
        red) and the Triangle Score downgrades when the ratio creeps past
        sector norms.
      </p>

      <h2>Trigger 3 — The original thesis is broken</h2>
      <p>
        You bought a stock for a reason. Write it down when you buy. Re-read it
        every annual report. If the reason is no longer true, the position is no
        longer the same investment.
      </p>
      <p>Common thesis-breakers:</p>
      <ul>
        <li><strong>Regulatory</strong> &mdash; energy company hit with a new carbon tax; tobacco company facing a regional flavour ban; bank facing higher capital requirements.</li>
        <li><strong>Technological</strong> &mdash; Kodak, Blockbuster, traditional media. The cash flow looks great until it suddenly doesn&apos;t.</li>
        <li><strong>Competitive</strong> &mdash; a new entrant takes 20% of the market in two years. Look at telecom’s response to streaming.</li>
        <li><strong>Management</strong> &mdash; an acquisition that lays waste to the balance sheet. AT&amp;T-Time Warner. Kraft Heinz integration. 3G Capital playbooks gone wrong.</li>
      </ul>

      <h2>Trigger 4 — Tax-loss harvest beats the future income</h2>
      <p>
        If you hold a dividend stock at a sizeable unrealised loss and you have
        capital gains elsewhere to offset, selling can convert a paper loss into
        a real tax savings of 15&ndash;37% of the loss. You can immediately buy
        a similar (but not substantially identical) name to keep the sector
        exposure.
      </p>
      <p>
        Worked example: you bought $20,000 of a regional bank that&apos;s now
        worth $14,000. If you have $6,000 of long-term gains elsewhere this
        year, harvesting the bank position offsets the gain dollar-for-dollar
        and saves you 15% &times; $6,000 = $900 in tax. Wash-sale rules and the
        31-day window are covered in{' '}
        <Link to="/learn/tax-loss-harvesting-playbook">the tax-loss harvesting
        playbook</Link>.
      </p>

      <h2>Trigger 5 — Sector concentration has grown dangerous</h2>
      <p>
        Winners get bigger. A 4% position that compounds at 18% a year while the
        rest of the portfolio compounds at 8% is a 9% position five years later.
        That&apos;s great for the wallet and dangerous for risk control.
      </p>
      <p>
        Rule of thumb: trim any single position back to its target weight when
        it exceeds 8&ndash;10% of the portfolio. You&apos;re not selling
        because the company got worse — you&apos;re selling because the
        position size got bigger than your risk tolerance.
      </p>
      <p>
        DiviDrip flags this via the <strong>Income Concentration banner</strong>{' '}
        on the Portfolio dashboard — it surfaces any single ticker
        contributing more than 30% of total annual dividends, which usually
        correlates with outsized position weight.
      </p>

      <h2>Trigger 6 — A clearly better opportunity</h2>
      <p>
        Capital allocation isn&apos;t static. If you hold a 3%-yielding stagnant
        utility and a comparable-risk 4%-yielding grower in the same sector
        emerges, swapping is rational. The bar is high though &mdash; the new
        name needs to be <em>materially</em> better, not 0.5% richer, to justify
        the tax friction in a taxable account.
      </p>
      <p>
        In tax-advantaged accounts (Roth, Traditional IRA, 401(k)) the friction
        is zero, so the bar is lower. Some dividend investors do an annual
        portfolio review every January for exactly this kind of optimisation.
      </p>

      <h2>What is NOT a sell trigger</h2>
      <ul>
        <li><strong>The price dropped 10%.</strong> Price wobbles happen quarterly. Sell when the business changes, not when the price moves.</li>
        <li><strong>The yield is now 3.2% instead of 4%.</strong> If the dividend grew and the price grew faster, that’s a feature, not a bug. Your Yield on Cost is still 4%+.</li>
        <li><strong>A pundit on financial TV said sell.</strong> If you would not buy this stock today at this price, that’s a reason to think. But thinking is not selling.</li>
        <li><strong>You’re bored.</strong> Boring is the point. See <Link to="/learn/boring-is-beautiful">why dividend growers beat glamour stocks</Link>.</li>
      </ul>

      <h2>How DiviDrip helps you decide</h2>
      <ol>
        <li>
          <strong>Dividend Triangle Score</strong> &mdash; If a holding’s
          Triangle has fallen from GREEN to AMBER to RED over 6+ months, that’s
          your six-month early warning. Fundamentals deteriorate before the
          dividend gets cut.
        </li>
        <li>
          <strong>Streak Risk Chart</strong> &mdash; Visual annual dividend per
          share. A flat line followed by a small dip is a Trigger-1 / Trigger-2
          situation forming.
        </li>
        <li>
          <strong>SEC Filings Alerts</strong> &mdash; The Notifications panel
          surfaces 8-K material events for your holdings. CEO changes,
          guidance cuts, and major write-offs often precede dividend trouble.
        </li>
        <li>
          <strong>Tax Lot Optimizer</strong> &mdash; If you decide to sell, the{' '}
          <Link to="/tax-lots">Tax Lots</Link> page lets you choose the
          highest-cost lots first to minimise your tax bill on the realised
          gain.
        </li>
      </ol>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }) => (
          <div key={q} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>

      <h2>Try it</h2>
      <p>
        Head to your <Link to="/dashboard?view=portfolio">Portfolio</Link>, click the{' '}
        <strong>Positions</strong> button on the My Portfolio card to jump to your
        stock rows, and look at each row&apos;s <strong>Triangle badge</strong> —
        the colored circle that sits right next to the ticker. Pay attention to
        any holding sitting at AMBER or RED. Click into the Stock Modal&rsquo;s
        Triangle tab to see the Streak Risk Chart and the trend across the last
        5 years. If the score has been deteriorating and the Streak chart shows
        growth flatlining, you&apos;re looking at a Trigger-2 forming — time to
        investigate, not panic, and certainly not ignore.
      </p>
    </LearnPage>
  );
}
